import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "emrkt-cookie-consent";
const STORAGE_VERSION = 1;

export type CookieCategories = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = {
  version: number;
  decidedAt: string;
  categories: CookieCategories;
};

const defaultCategories: CookieCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function getStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(categories: CookieCategories) {
  const payload: StoredConsent = {
    version: STORAGE_VERSION,
    decidedAt: new Date().toISOString(),
    categories,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("emrkt:cookie-consent", { detail: payload }));
}

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [categories, setCategories] = useState<CookieCategories>(defaultCategories);

  useEffect(() => {
    if (!getStoredConsent()) setOpen(true);
    const onOpen = () => {
      const existing = getStoredConsent();
      if (existing) setCategories(existing.categories);
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener("emrkt:open-cookie-preferences", onOpen);
    return () => window.removeEventListener("emrkt:open-cookie-preferences", onOpen);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setOpen(false);
  };
  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setOpen(false);
  };
  const savePrefs = () => {
    saveConsent(categories);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
            <Cookie className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-lg font-semibold text-foreground">
                Your cookie preferences
              </h2>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              We use cookies to keep you signed in, remember preferences, and understand how the
              Service is used. Read our{" "}
              <a href="/cookies" className="underline hover:text-foreground">
                Cookie Policy
              </a>
              .
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <Row
                  title="Strictly necessary"
                  desc="Required for sign-in, security, and core functionality. Always on."
                  checked
                  disabled
                />
                <Row
                  title="Analytics"
                  desc="Anonymous usage and error monitoring (e.g., Sentry) so we can improve the Service."
                  checked={categories.analytics}
                  onChange={(v) => setCategories((c) => ({ ...c, analytics: v }))}
                />
                <Row
                  title="Marketing"
                  desc="Used for measuring campaigns. We do not currently set marketing cookies."
                  checked={categories.marketing}
                  onChange={(v) => setCategories((c) => ({ ...c, marketing: v }))}
                />
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              {!showDetails && (
                <Button variant="ghost" size="sm" onClick={() => setShowDetails(true)}>
                  Customize
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={rejectAll}>
                Reject non-essential
              </Button>
              {showDetails ? (
                <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={savePrefs}>
                  Save preferences
                </Button>
              ) : (
                <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={acceptAll}>
                  Accept all
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={(v) => onChange?.(Boolean(v))}
      aria-label={title}
    />
  </div>
);

export default CookieConsent;